import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ParticipatingEvent,
  getAllParticipatingEvents,
  deleteParticipatingEvent,
} from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import './AdminPromotions.css';

type ParticipatingConfirmState = null | { kind: 'delete_event'; eventId: number };

const LIST_PATH = '/admin/participating-events';

const AdminParticipatingEventsList: React.FC = () => {
  const [events, setEvents] = useState<ParticipatingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmState, setConfirmState] = useState<ParticipatingConfirmState>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const eventsRes = await getAllParticipatingEvents();
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error loading participating events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const requestDeleteEvent = (eventId: number) => {
    setConfirmState({ kind: 'delete_event', eventId });
  };

  const handleConfirmDelete = async () => {
    if (!confirmState || confirmState.kind !== 'delete_event') return;
    try {
      await deleteParticipatingEvent(confirmState.eventId);
      setEvents((prev) => prev.filter((e) => e.id !== confirmState.eventId));
      setConfirmState(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  const seatsLeft = (event: ParticipatingEvent) =>
    event.total_seats != null ? event.total_seats - event.booked_seats : null;

  if (loading) {
    return <div className="loading">Loading participating events...</div>;
  }

  return (
    <div className="admin-promotions">
      <div className="promotions-header">
        <h2>Participating Events</h2>
        <Link to={`${LIST_PATH}/new`} className="add-button" style={{ textDecoration: 'none', display: 'inline-block' }}>
          + Add Event
        </Link>
      </div>

      <div className="promotions-table-container">
        <table className="promotions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Date</th>
              <th>Location</th>
              <th>Color</th>
              <th>Price</th>
              <th>Seats</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty-state">
                  No participating events yet. Use &quot;Add Event&quot; to create one.
                </td>
              </tr>
            ) : (
              events.map((event) => (
                <tr key={event.id}>
                  <td>{event.id}</td>
                  <td className="title-cell">{event.title}</td>
                  <td>{event.event_date ? event.event_date.split('T')[0] : '-'}</td>
                  <td>{event.location || '-'}</td>
                  <td>
                    {event.button_color ? (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          backgroundColor: event.button_color,
                          border: '1px solid #ccc',
                          verticalAlign: 'middle',
                        }}
                        title={event.button_color}
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{event.price || '-'}</td>
                  <td>
                    <div className="seats-cell">
                      {event.total_seats != null ? (
                        <>
                          <span className="seats-main">
                            {event.booked_seats}/{event.total_seats}
                          </span>
                          <span className="hint">({seatsLeft(event)} left)</span>
                        </>
                      ) : (
                        <span className="seats-main">Unlimited</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={
                        event.is_active === 1 ? 'status-badge active' : 'status-badge inactive'
                      }
                    >
                      {event.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link to={`${LIST_PATH}/${event.id}/edit`} className="edit-button" style={{ textDecoration: 'none' }}>
                        Edit
                      </Link>
                      <Link to={`${LIST_PATH}/${event.id}/interests`} className="view-button" style={{ textDecoration: 'none' }}>
                        View Interested
                      </Link>
                      <button
                        type="button"
                        className="delete-button"
                        onClick={() => requestDeleteEvent(event.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {confirmState && (
        <ConfirmDialog
          open
          title="Delete event"
          message="Are you sure you want to delete this event? All interest records for this event will be removed. This cannot be undone."
          confirmLabel="Delete event"
          cancelLabel="Cancel"
          danger
          onCancel={() => setConfirmState(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default AdminParticipatingEventsList;
